import type { CallLogType } from 'Components/CallLogs/types';
import { useNotificationStore } from 'Modules/notification/store';
import React from 'react';
import {
  ANALYTICS_EVENT_CALL_LOG_DESELECTED,
  ANALYTICS_EVENT_CALL_LOG_REACH_LIMIT,
  ANALYTICS_EVENT_CALL_LOG_SELECT_ALL,
  ANALYTICS_EVENT_CALL_LOG_SELECTED,
  ANALYTICS_EVENT_CALL_LOG_SELECTION_STARTED,
  pushToGTMDataLayer,
} from 'Utils/analytics';

// Maximum number of call logs that can be selected at once.
export const MAX_SELECTABLE_CALL_LOGS = 100;

interface UseCallLogSelectionReturn {
  selectedIds: Set<CallLogType['id']>;
  selectedCount: number;
  isSelecting: boolean;
  isMaxReached: boolean;
  startSelecting: () => void;
  cancelSelecting: () => void;
  toggleSelect: (id: CallLogType['id']) => void;
  selectAllVisible: (visibleLogs: CallLogType[]) => void;
  resetSelection: () => void;
  replaceSelection: (ids: CallLogType['id'][]) => void;
}

/**
 * Manages selection state for bulk actions in the call logs list.
 *
 * Supports individual selection, selecting visible logs, and a maximum
 * selection limit.
 */
export const useCallLogSelection = (): UseCallLogSelectionReturn => {
  const [selectedIds, setSelectedIds] = React.useState<Set<CallLogType['id']>>(
    new Set()
  );

  // Keep selection mode separate from selected ids so zero selected items
  // remains a valid state.
  const [isSelecting, setIsSelecting] = React.useState(false);

  // Keep the latest selection available to handlers without relying on
  // state updater callbacks for analytics and notifications.
  const selectedIdsRef = React.useRef(selectedIds);

  // Show feedback when the selection limit is reached.
  const notifyLimitReached = () => {
    pushToGTMDataLayer(ANALYTICS_EVENT_CALL_LOG_REACH_LIMIT);
    useNotificationStore.getState().addNotify({
      type: 'info',
      autoDismiss: true,
      dismissible: true,
      title: 'Selection limit reached',
      message: `You can select up to ${MAX_SELECTABLE_CALL_LOGS} call logs at a time.`,
    });
  };

  // Toggle selection for a single call log.
  const toggleSelect = (id: CallLogType['id']) => {
    const prev = selectedIdsRef.current;

    if (prev.has(id)) {
      const next = new Set(prev);
      next.delete(id);
      selectedIdsRef.current = next;
      setSelectedIds(next);
      pushToGTMDataLayer(ANALYTICS_EVENT_CALL_LOG_DESELECTED);

      // Deselecting the last remaining item exits selection mode entirely
      if (next.size === 0) {
        setIsSelecting(false);
      }
      return;
    }

    if (prev.size >= MAX_SELECTABLE_CALL_LOGS) {
      notifyLimitReached();
      return;
    }

    const wasEmpty = prev.size === 0;
    const next = new Set(prev);
    next.add(id);
    selectedIdsRef.current = next;
    setSelectedIds(next);
    // First selection fires a different event than subsequent ones.
    pushToGTMDataLayer(
      wasEmpty
        ? ANALYTICS_EVENT_CALL_LOG_SELECTION_STARTED
        : ANALYTICS_EVENT_CALL_LOG_SELECTED
    );
  };

  // Select visible logs without exceeding the selection limit.
  const selectAllVisible = (visibleLogs: CallLogType[]) => {
    const prev = selectedIdsRef.current;
    const next = new Set(prev);
    const wasEmpty = next.size === 0;
    let reachedLimit = false;

    for (const log of visibleLogs) {
      if (next.size >= MAX_SELECTABLE_CALL_LOGS) {
        // Only show the limit warning if this action tries to select an
        // additional log beyond the limit.
        if (!next.has(log.id)) {
          reachedLimit = true;
        }
        break;
      }
      next.add(log.id);
    }

    selectedIdsRef.current = next;
    setSelectedIds(next);

    if (reachedLimit) {
      notifyLimitReached();
    }

    pushToGTMDataLayer(ANALYTICS_EVENT_CALL_LOG_SELECT_ALL);
    if (wasEmpty && next.size > 0) {
      pushToGTMDataLayer(ANALYTICS_EVENT_CALL_LOG_SELECTION_STARTED);
    }
  };

  // Clear the current selection without leaving selection mode.
  const resetSelection = () => {
    const next = new Set<CallLogType['id']>();
    selectedIdsRef.current = next;
    setSelectedIds(next);
  };

  // Replace the current selection, used to keep only failed ids after a
  // partial bulk-delete failure.
  const replaceSelection = (ids: CallLogType['id'][]) => {
    const next = new Set(ids);
    selectedIdsRef.current = next;
    setSelectedIds(next);
  };

  // Enter selection mode with an empty selection.
  const startSelecting = () => {
    const next = new Set<CallLogType['id']>();
    selectedIdsRef.current = next;
    setSelectedIds(next);
    setIsSelecting(true);
  };

  // Exit selection mode and clear the current selection.
  const cancelSelecting = () => {
    const next = new Set<CallLogType['id']>();
    selectedIdsRef.current = next;
    setIsSelecting(false);
    setSelectedIds(next);
  };

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    isSelecting,
    isMaxReached: selectedIds.size >= MAX_SELECTABLE_CALL_LOGS,
    startSelecting,
    cancelSelecting,
    toggleSelect,
    selectAllVisible,
    resetSelection,
    replaceSelection,
  };
};